import { Box, Stack, Typography } from "@mui/joy";
import ButtonWithModal from "components/common/ButtonWithModal";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import { useNavigate } from "react-router-dom";
import { useResetRecoilState } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { programDataAtom } from "recoil/programDataAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { userAtom, UserType } from "recoil/userAtom";
import { sendMessage } from "utils/webview";
import mixpanel from "mixpanel-browser";
import { onboardingDataState } from "../../recoil/onboardingDataAtom";
import LinkV2 from "../common/LinkV2";
import { theme } from "../../styles/theme";
import { isDev } from "../../config";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";
import useIsTabExists from "../../hooks/useIsTabExists";
import useIsInappWebview from "../../hooks/useIsInappWebview";

export default function UserInfo({ user }: { user: UserType }) {
  const { isSubscribing } = useSubscriptionStatus();

  const navigate = useNavigate();
  const resetUser = useResetRecoilState(userAtom);
  const resetPid = useResetRecoilState(patientAtom);
  const resetProgramData = useResetRecoilState(programDataAtom);
  const resetTaskState = useResetRecoilState(taskListAtom);
  const resetOnboardingData = useResetRecoilState(onboardingDataState);
  const resetHomeThoughtRecordState = useResetRecoilState(
    homeThoughtRecordListAtom
  );
  const isTabExists = useIsTabExists();
  const isInappWebview = useIsInappWebview();

  const initializeUser = () => {
    resetUser();
    resetPid();
    resetProgramData();
    resetTaskState();
    resetOnboardingData();
    resetHomeThoughtRecordState();

    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "set_app_lock",
          params: { isEnabled: "false" },
        })
      );

      sendMessage(
        JSON.stringify({
          event: "logout",
        })
      );
    }

    mixpanel.reset();

    window.location.href = `${window.location.origin}/login`;
  };

  // 계정 삭제 전용 초기화: 모든 Storage 삭제 포함
  const initializeUserAfterDeletion = () => {
    // 1) Recoil 상태 초기화 (onSet에 의해 로컬스토리지에 쓰는 atom이 있어도 이후 clear로 제거)
    resetUser();
    resetPid();
    resetProgramData();
    resetTaskState();
    resetOnboardingData();
    resetHomeThoughtRecordState();

    // 2) 인앱일 경우 웹뷰에 로그아웃/앱락 해제 신호 전달
    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "set_app_lock",
          params: { isEnabled: "false" },
        })
      );

      sendMessage(
        JSON.stringify({
          event: "logout",
        })
      );
    }

    // 3) 분석 식별자 초기화
    mixpanel.reset();

    // 4) 모든 스토리지 제거 (로컬/세션)
    try {
      localStorage.clear();
    } catch {}
    try {
      sessionStorage.clear();
    } catch {}

    // 5) 로그인 페이지로 이동
    window.location.href = `${window.location.origin}/login`;
  };

  return isTabExists ? (
    <Box onClick={() => navigate("/mypage/account")}>
      <LinkV2 text={"계정"} />
    </Box>
  ) : (
    <ButtonWithModal
      buttonText={""}
      size="lg"
      title={"계정"}
      textAlign="center"
      render={<LinkV2 text={"계정"} />}
      content={
        <Stack
          alignItems={"center"}
          spacing={"24px"}
          sx={{ width: "100%", color: "text.tertiary" }}
        >
          <Stack spacing={"12px"}>
            {[
              { title: "닉네임", content: user.nickname },
              ...(user.role === "patient"
                ? [{ title: "회원 번호", content: user.userId }]
                : []),
            ].map(({ title, content }) => (
              <Stack
                direction={"row"}
                justifyContent="space-between"
                gap="8px"
                alignItems="center"
                sx={{ width: "224px", whiteSpace: "nowrap" }}
              >
                <Typography
                  variant={"Body"}
                  sx={{
                    color: theme.vars.palette.Content.Medium,
                    fontWeight: 500,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant={"Body"}
                  sx={{
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {content}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            direction={"row"}
            justifyContent="center"
            alignItems="center"
            spacing="20px"
          >
            <ButtonWithModal
              render={<LinkV2 text={"로그아웃"} />}
              buttonText={"로그아웃"}
              fetchActionUrl={"/user/logout"}
              title={"정말로 로그아웃할까요?"}
              actionText={"모든 기기와 브라우저에서 로그아웃돼요."}
              actionButtonColor={"danger"}
              actionAfterFetch={initializeUser}
            />
            <ButtonWithModal
              render={<LinkV2 text={"계정 삭제"} />}
              buttonText={"계정 삭제"}
              {...(!isDev &&
                isSubscribing && {
                  title: "지금은 삭제할 수 없습니다",
                  subtitle: "프로그램 이용 종료 후 다시 시도해주세요.",
                })}
              {...((isDev || !isSubscribing) && {
                title: "정말로 삭제할까요?",
                subtitle:
                  "모든 정보와 지난 활동 내역이 삭제되며 돌이킬 수 없어요.",
                actionText: "삭제하기",
                fetchActionUrl: "/user/leave",
                actionAfterFetch: initializeUserAfterDeletion,
                actionButtonColor: "danger",
              })}
            />
          </Stack>
        </Stack>
      }
    />
  );
}
